import {
  Alert,
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useTheme,
  Checkbox,
  alpha,
  Container,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import PrimaryButton from "../../components/button/button";
import CenteredBox from "../../components/centeredBox/centeredBox";
import Loading from "../../components/loading/loading";
import { clearUserState, signup } from "../../state/slices/user";
import validatePassword from "../../utils/passwordValidator";
import { updateContentType } from "../../config/config";
import { Person, Email, Phone, Lock, PersonAdd } from "@mui/icons-material";

function Signup() {
  let theme = useTheme();
  let userState = useSelector((state) => state.user);
  let navigate = useNavigate();
  let dispatch = useDispatch();
  let params = useParams();
  let location = useLocation();
  const { t } = useTranslation();

  let roles = {
    Student: "STUDENT",
    Parent: "PARENT",
    Tutor: "TUTOR",
  };

  // Get role from URL params or default to "Tutor"
  const getRoleFromURL = () => {
    const searchParams = new URLSearchParams(location.search);
    const roleParam = searchParams.get('role');
    if (roleParam && ['Tutor', 'Student', 'Parent'].includes(roleParam)) {
      return roleParam;
    }
    return "Tutor";
  };

  const [selectedRole, setSelectedRole] = useState(getRoleFromURL());

  // Update role when URL changes
  useEffect(() => {
    const roleFromURL = getRoleFromURL();
    if (roleFromURL !== selectedRole) {
      setSelectedRole(roleFromURL);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const handleRoleChange = (event, newRole) => {
    if (newRole !== null) {
      setSelectedRole(newRole);
    }
  };

  let [submitStatus, setSubmitStatus] = useState("");

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    checked: false,
  });

  const [errors, setErrors] = useState({});

  if (userState.successMsg) {
    dispatch(clearUserState());
    navigate("/confirmation");
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    // Perform validation on the individual field as the user types
    validateField(e.target.name, e.target.value);
  };

  const validateField = (fieldName, value) => {
    const validationErrors = { ...errors };

    switch (fieldName) {
      case "firstName":
        validationErrors.firstName = value.trim()
          ? String(value).length < 3
            ? t('validation.firstNameTooShort')
            : ""
          : t('validation.firstNameRequired');
        break;
      case "lastName":
        validationErrors.lastName = value.trim()
          ? String(value).length < 3
            ? t('validation.lastNameTooShort')
            : ""
          : t('validation.lastNameRequired');
        break;
      case "email":
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        validationErrors.email =
          value.trim() && emailRegex.test(value) ? "" : t('validation.invalidEmail');
        break;
      case "phoneNumber":
        const phoneRegex = /^\+251\d{9}$/;
        validationErrors.phoneNumber =
          value.trim() && phoneRegex.test(value) ? "" : t('validation.phoneNumberInvalid');
        break;
      case "password":
        validationErrors.password = value.trim()
          ? validatePassword(String(value))
          : t('validation.passwordRequired');
        break;
      case "confirmPassword":
        validationErrors.confirmPassword =
          value === formData.password ? "" : t('validation.passwordsDoNotMatch');
        break;
      case "checked":
        validationErrors.checked = value
          ? ""
          : t('validation.termsRequired');
        break;
      default:
        break;
    }

    setErrors((prev) => {
      return { ...prev, ...validationErrors };
    });
    // console.log(errors, 'in validation', validationErrors)
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const validationErrors = {};

    Object.keys(formData).forEach((fieldName) => {
      validateField(fieldName, formData[fieldName]);
      if (errors[fieldName]) {
        validationErrors[fieldName] = errors[fieldName];
      }
    });

    setSubmitStatus((prev) => prev + "x");
  };

  useEffect(() => {
    console.log("in effect", errors, submitStatus);
    if (
      submitStatus &&
      Object.keys(errors).filter((key) => errors[key]).length === 0
    ) {
      let userData = new FormData();
      userData.set("firstName", JSON.stringify(formData.firstName));
      userData.set("lastName", JSON.stringify(formData.lastName));
      userData.set("email", JSON.stringify(formData.email));
      userData.set("phoneNumber", JSON.stringify(formData.phoneNumber));
      userData.set("password", JSON.stringify(formData.password));
      userData.set("roles", JSON.stringify([roles[selectedRole]]));

      updateContentType("multipart/form-data")
      dispatch(signup(userData));
      updateContentType("application/json")
      console.log("Form submitted:", formData);
    } else {
      dispatch(clearUserState());
    }
  }, [selectedRole, submitStatus]);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: 'flex',
        alignItems: 'center',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 4,
      }}
    >
      <Container maxWidth="lg">
        <Grid container spacing={0} alignItems="stretch">
          {/* Left Image Section */}
          <Grid item xs={12} md={4} sx={{ display: { xs: 'none', md: 'block' } }}>
            <Box
              sx={{
                position: 'relative',
                height: '100%',
                minHeight: '550px',
                borderRadius: '4px 0 0 4px',
                overflow: 'hidden',
                boxShadow: `0 10px 40px ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <img
                src={
                  selectedRole === "Student"
                    ? `/STUDENT.jpeg`
                    : selectedRole === "Parent"
                      ? `/PARENT.jpg`
                      : `/TUTOR.png`
                }
                alt="Sign Up"
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
              />
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: `linear-gradient(to bottom, ${alpha(theme.palette.primary.main, 0.3)} 0%, ${alpha(theme.palette.primary.main, 0.1)} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 4,
                }}
              >
                <Box sx={{ textAlign: 'center', color: 'white' }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <PersonAdd sx={{ fontSize: 50, opacity: 0.95 }} />
                    <Box
                      sx={{
                        px: 3,
                        py: 1,
                        borderRadius: 3,
                        bgcolor: alpha('#ffffff', 0.2),
                        backdropFilter: 'blur(10px)',
                      }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {selectedRole === "Student"
                          ? t('signup.forStudents')
                          : selectedRole === "Tutor"
                            ? t('signup.forTutors')
                            : t('signup.forParents')}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
                    {t('signup.joinToday')}
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.95, lineHeight: 1.8, fontSize: '1.1rem' }}>
                    {selectedRole === "Student"
                      ? t('signup.studentSubtitle')
                      : selectedRole === "Tutor"
                        ? t('signup.tutorSubtitle')
                        : t('signup.parentSubtitle')}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Grid>

          {/* Right Signup Form */}
          <Grid item xs={12} md={8} sx={{ display: 'flex' }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: '0 4px 4px 0',
                p: { xs: 3, md: 4 },
                background: 'white',
                boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              {/* Header Section */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: theme.palette.primary.main,
                    mb: 1,
                  }}
                >
                  {t('signup.signUp')}
                </Typography>

                {/* Role Switcher */}
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2, mt: 2 }}>
                  <ToggleButtonGroup
                    value={selectedRole}
                    exclusive
                    onChange={handleRoleChange}
                    aria-label="user role"
                    sx={{
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                      borderRadius: 3,
                      p: 0.5,
                      '& .MuiToggleButton-root': {
                        border: 'none',
                        borderRadius: 2.5,
                        px: 3,
                        py: 1,
                        textTransform: 'capitalize',
                        fontWeight: 600,
                        color: 'text.secondary',
                        transition: 'all 0.2s ease',
                        '&.Mui-selected': {
                          bgcolor: 'white',
                          color: theme.palette.primary.main,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                          '&:hover': {
                            bgcolor: 'white',
                          }
                        },
                        '&:hover': {
                          bgcolor: alpha(theme.palette.primary.main, 0.08),
                        }
                      }
                    }}
                  >
                    <ToggleButton value="Tutor">
                      {t('navbar.tutors') || "Tutors"}
                    </ToggleButton>
                    <ToggleButton value="Student">
                      {t('navbar.students') || "Students"}
                    </ToggleButton>
                    <ToggleButton value="Parent">
                      {t('navbar.parents') || "Parents"}
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Box>

                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  {selectedRole === "Student"
                    ? t('signup.studentSubtitle')
                    : selectedRole === "Tutor"
                      ? t('signup.tutorSubtitle')
                      : t('signup.parentSubtitle')}
                </Typography>
              </Box>

              <Stack spacing={2.5}>
                <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingRight: { xs: '0 !important', sm: '8px !important' }, mb: { xs: 2, sm: 0 } }}>
                    <TextField
                      label={t('signup.firstName')}
                      variant="outlined"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      error={!!errors.firstName}
                      helperText={errors.firstName}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            }
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white',
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingLeft: { xs: '0 !important', sm: '8px !important' }, mb: { xs: 2, sm: 0 } }}>
                    <TextField
                      label={t('signup.lastName')}
                      variant="outlined"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      error={!!errors.lastName}
                      helperText={errors.lastName}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Person sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            }
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white',
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Email and Phone Number Row */}
                <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingRight: { xs: '0 !important', sm: '8px !important' }, mb: { xs: 2, sm: 0 } }}>
                    <TextField
                      label={t('common.email')}
                      variant="outlined"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      error={!!errors.email}
                      helperText={errors.email}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            }
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white',
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingLeft: { xs: '0 !important', sm: '8px !important' }, mb: { xs: 2, sm: 0 } }}>
                    <TextField
                      label={t('signup.phoneNumber')}
                      variant="outlined"
                      name="phoneNumber"
                      placeholder="+251XXXXXXXXX"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      error={!!errors.phoneNumber}
                      helperText={errors.phoneNumber}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            }
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white',
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {/* Password and Confirm Password Row */}
                <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingRight: { xs: '0 !important', sm: '8px !important' }, mb: { xs: 2, sm: 0 } }}>
                    <TextField
                      label={t('common.password')}
                      variant="outlined"
                      name="password"
                      type="password"
                      value={formData.password}
                      onChange={handleChange}
                      error={!!errors.password}
                      helperText={errors.password}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            }
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white',
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                          }
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} sx={{ width: '100%', boxSizing: 'border-box', padding: '0 !important', paddingLeft: { xs: '0 !important', sm: '8px !important' }, mb: { xs: 2, sm: 0 } }}>
                    <TextField
                      label={t('signup.confirmPassword')}
                      variant="outlined"
                      name="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      error={!!errors.confirmPassword}
                      helperText={errors.confirmPassword ? errors.confirmPassword : formData.confirmPassword ? t('validation.passwordMatched') : ""}
                      fullWidth
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock sx={{ color: theme.palette.primary.main, opacity: 0.7 }} />
                          </InputAdornment>
                        ),
                      }}
                      FormHelperTextProps={{
                        sx: {
                          color: formData.confirmPassword && !errors.confirmPassword ? 'success.main' : 'inherit',
                        }
                      }}
                      sx={{
                        width: '100%',
                        boxSizing: 'border-box',
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.03),
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.05),
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: theme.palette.primary.main,
                            }
                          },
                          '&.Mui-focused': {
                            bgcolor: 'white',
                            boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.15)}`,
                          }
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {errors.checked && (
                  <Alert severity="error" sx={{ borderRadius: 3 }}>
                    {errors.checked}
                  </Alert>
                )}

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`,
                  }}
                >
                  <Checkbox
                    checked={formData.checked}
                    onChange={(e) =>
                      setFormData({ ...formData, checked: e.target.checked })
                    }
                    inputProps={{ "aria-label": "controlled" }}
                    sx={{ mt: -0.5 }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.secondary', mt: 0.5 }}
                  >
                    {t('signup.agreeTerms')}{" "}
                    <Typography
                      sx={{
                        cursor: "pointer",
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        "&:hover": {
                          textDecoration: "underline",
                        },
                      }}
                      component={Link}
                      to="/terms"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t('signup.termsAndConditions')}
                    </Typography>
                  </Typography>
                </Box>

                {userState.errorMsg && (
                  <Alert severity="error" sx={{ borderRadius: 3 }}>
                    {userState.errorMsg.toLowerCase().includes('forgot password') ? (
                      <Typography component="span">
                        {userState.errorMsg.split(/forgot password/i)[0]}
                        <Link
                          to="/forgot-password"
                          style={{
                            color: 'inherit',
                            fontWeight: 700,
                            textDecoration: 'underline',
                            cursor: 'pointer',
                          }}
                        >
                          {userState.errorMsg.match(/forgot password/i)?.[0] || 'forgot password'}
                        </Link>
                        {userState.errorMsg.split(/forgot password/i)[1]}
                      </Typography>
                    ) : (
                      userState.errorMsg
                    )}
                  </Alert>
                )}

                <Button
                  onClick={handleSubmit}
                  disabled={userState.loading}
                  fullWidth
                  size="large"
                  sx={{
                    background: theme.palette.primary.main,
                    color: theme.palette.secondary.main,
                    textTransform: "capitalize",
                    py: 1.75,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '1.1rem',
                    boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.4)}`,
                      background: theme.palette.primary.dark,
                    },
                    '&:disabled': {
                      background: alpha(theme.palette.primary.main, 0.5),
                      transform: 'none',
                    }
                  }}
                  disableElevation
                  variant="contained"
                >
                  {userState.loading ? (
                    <CircularProgress size={25} color="inherit" />
                  ) : (
                    <>
                      <PersonAdd sx={{ mr: 1.5, fontSize: 22 }} />
                      {t('signup.signUp')}
                    </>
                  )}
                </Button>

                <Box sx={{ textAlign: 'center', mt: 1 }}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('signup.alreadyHaveAccount')}{" "}
                    <Link
                      to="/login"
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 700,
                        textDecoration: 'none',
                      }}
                    >
                      {t('signup.login')}
                    </Link>
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default Signup;
